import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon } from 'lucide-react';
import { Wordmark } from './Brand';
import { TryFreeLink } from './TryFreeLink';
const navItems = [{
  to: '/worlds',
  label: 'Worlds'
}, {
  to: '/parents',
  label: 'For parents'
}, {
  to: '/pricing',
  label: 'Pricing'
}, {
  to: '/support',
  label: 'Support'
}];

/**
 * On the homepage the big centered wordmark is the brand, so the header is
 * just quiet corner links. Every other page gets the compact branded bar.
 */
export function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isHome = location.pathname === '/';

  // Close the menu on any navigation, including hash jumps.
  useEffect(() => {
    setOpen(false);
  }, [location.key]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const linkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `whitespace-nowrap text-[16px] font-bold transition-colors duration-150 ease-out hover:text-ink ${isActive ? 'text-ink underline decoration-coral decoration-[3px] underline-offset-8' : 'text-ink/75'}`;
  return <header className={isHome ? 'absolute inset-x-0 top-0 z-40' : 'sticky top-0 z-40 border-b border-ink/10 bg-lavender'} data-id="element-266" data-mp="2cub95qf1nncg">
      <div className={`mx-auto flex items-center gap-4 px-4 sm:px-6 ${isHome ? 'h-16 max-w-[1240px] justify-end' : 'h-[76px] max-w-content justify-between'}`} data-id="element-267" data-mp="282a1yiwqgmta">
        {!isHome && <Link to="/" aria-label="Lightning Learn, home" className="-my-1 shrink-0 rounded-xl" data-id="element-268" data-mp="30m8m5ku8clf5">
            <Wordmark decorative eager className="h-[64px] w-[96px] sm:h-[70px] sm:w-[105px]" data-id="element-269" data-mp="2vrvzz31dcvzp" />
          </Link>}

        <nav aria-label="Main" className="hidden items-center gap-7 md:flex" data-id="element-270" data-mp="xxiemrtvyskb">
          {navItems.map(item => <NavLink key={item.to} to={item.to} className={linkClass} data-id="element-271" data-mp="k758mv0k1nxm">
              {item.label}
            </NavLink>)}
          {!isHome && <TryFreeLink size="sm" data-id="element-272" data-mp="p3lshd0q1g4w" />}
        </nav>

        <button ref={buttonRef} type="button" onClick={() => setOpen(value => !value)} className="inline-flex h-11 w-11 items-center justify-center rounded-xl border-2 border-ink bg-white text-ink md:hidden" aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? 'Close menu' : 'Open menu'} data-id="element-273" data-mp="a7o1e95nn1zj">
          {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-274" data-mp="1vkzao82jeuuc" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-275" data-mp="1vmaajwsv3ig6" />}
        </button>
      </div>

      {open && <nav id="mobile-nav" aria-label="Main" className="absolute inset-x-3 top-full z-50 rounded-3xl border-2 border-ink bg-white px-5 pb-5 pt-2 shadow-lift md:hidden" data-id="element-276" data-mp="20iiwpu5h8bwu">
          <ul data-id="element-277" data-mp="25nmfl9ydf5kc">
            {navItems.map(item => <li key={item.to} data-id="element-278" data-mp="2jhnga98vv2nc">
                <NavLink to={item.to} onClick={() => setOpen(false)} className="block border-b border-ink/10 py-3.5 font-display text-[20px] text-ink" data-id="element-279" data-mp="2jiyhq2euvyba">
                  {item.label}
                </NavLink>
              </li>)}
          </ul>
          <TryFreeLink className="mt-5 w-full" onClick={() => setOpen(false)} data-id="element-280" data-mp="32fg8aghyxs5y" />
        </nav>}
    </header>;
}